/*
 * Cart discounts
 * ---------------------------------------------------------------------------
 * Adding and removing discount codes from the cart, without leaving the page.
 *
 * ONE request does all of it. The Ajax Cart API has taken discount codes since
 * May 2025:
 *
 *   POST /cart/update.js    discount=WELCOME25,SAVE10
 *
 * which sets the session's code list to EXACTLY those codes -- so `discount=`
 * clears them, and leaving one out is how a code is removed -- and answers with
 * the recomputed cart, including `applicable` per code. The theme's own
 * `discount_codes` attribute rides along in the same POST, because Liquid has
 * no cart.discount_codes to read back: a code Shopify has not priced onto the
 * lines is known to the theme alone.
 *
 * Then `cart:refresh`, the event the quantity controls already use, re-renders
 * the drawer -- and the cart page -- in place. Nothing navigates.
 *
 * WHAT THIS REPLACED, AND WHY, because both were wrong in a way that looked
 * right:
 *
 *   Applying used to navigate to /discount/<codes>, which reloaded the page and
 *   landed the shopper on the cart. Worse than it sounds: the drawer is
 *   re-fetched from /cart?view=mini-cart on every cart change, and in that
 *   render request.path IS /cart -- so the return-to became the cart page even
 *   for a shopper who was reading a collection. The drawer closed, the page
 *   reloaded, they lost their place.
 *
 *   Removing used to re-send the remaining codes to /discount/ and expire the
 *   `discount_code` cookie. Neither removes anything. /discount/ ACCUMULATES --
 *   WELCOME25 then SAVE10 leaves both on the cart -- and the cookie only ever
 *   holds the last one, which is what made it look like a replacement. The set
 *   itself lives in `_shopify_essential`, which is HttpOnly and beyond JS. So
 *   the old remove de-listed a code the shopper was still going to get at
 *   checkout.
 *
 * A code Shopify will not take is not left sitting in the session either: the
 * response says `applicable: false`, so the list is re-sent without it and the
 * shopper is told, here, instead of finding out at checkout.
 *
 * /discount/ is still right for ONE thing -- the footer's claim link. It
 * accumulates, which is exactly what a link adding the welcome code to whatever
 * the shopper already has wants, and as a plain <a> it works with no JavaScript
 * at all. With JavaScript, claim() below fetches it rather than following it, so
 * that click keeps the shopper's place too.
 *
 * Speed, since it is the whole point: the code appears in the list the moment it
 * is typed rather than after the round trip, the pending look waits 180ms so a
 * fast cart never flashes a spinner, and a refused code costs no re-render at
 * all -- the list it would re-render to is the one already on screen.
 */
(function () {
  'use strict';

  var ATTRIBUTE = 'discount_codes';

  /* Long enough that a cart answering in 80ms never flickers, short enough that
     a slow one does not feel unanswered. */
  var BUSY_AFTER = 180;

  /* The refresh reports itself through the header's own event; the ceiling is
     only so a lost event cannot leave a row pending forever. */
  var REFRESH_CEILING = 1200;

  function parseCodes(raw) {
    return String(raw || '')
      .split(',')
      .map(function (code) { return code.trim().toUpperCase(); })
      .filter(function (code) { return code.length > 0; })
      .filter(function (code, index, all) { return all.indexOf(code) === index; });
  }

  function sameSet(a, b) {
    if (a.length !== b.length) { return false; }
    return a.every(function (code) { return b.indexOf(code) !== -1; });
  }

  function text(root, name, code, discount) {
    var raw = root.getAttribute('data-text-' + name) || '';
    if (code) { raw = raw.replace('[code]', code); }
    if (discount) { raw = raw.replace('[discount]', discount); }
    return raw;
  }

  /* ----------------------------------------------------------------- the API */

  function updateUrl(root) {
    return (root.getAttribute('data-cart-update-url') || '/cart/update') + '.js';
  }

  /* `discount` is the whole list, every time, because it SETS the list rather
     than adding to it. The attribute goes in the same POST: two records of one
     truth, written in one move, so a re-render cannot read a stale one. */
  function writeCodes(root, codes) {
    var body = new URLSearchParams();
    body.append('discount', codes.join(','));
    body.append('attributes[' + ATTRIBUTE + ']', codes.join(','));

    return fetch(updateUrl(root), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString(),
      credentials: 'same-origin'
    }).then(function (response) {
      if (!response.ok) { throw new Error('cart update rejected'); }
      return response.json();
    });
  }

  /* The codes Shopify answered `applicable: false` for -- one that does not
     exist, or that does not apply to this cart. */
  function refusedBy(cart, codes) {
    var answered = (cart && cart.discount_codes) || [];
    var refused = answered
      .filter(function (entry) { return entry && entry.applicable === false; })
      .map(function (entry) { return String(entry.code || '').toUpperCase(); });

    return codes.filter(function (code) { return refused.indexOf(code) !== -1; });
  }

  /* An automatic ORDER discount already on the cart, by title. Shopify gives a
     cart one order discount unless they are set to combine, and answers
     `applicable: false` for a code that lost to a better automatic -- the same
     answer as for a code that does not exist, so this is how the two are told
     apart. Line-level automatics (a brand deal) combine and are not the cause. */
  function automaticOrderDiscount(cart) {
    var applied = (cart && cart.cart_level_discount_applications) || [];
    for (var i = 0; i < applied.length; i++) {
      var application = applied[i] && applied[i].discount_application;
      if (application && application.type === 'automatic' && application.title) {
        return application.title;
      }
    }
    return '';
  }

  function refresh() {
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  /* Both hosts replace the block wholesale on a refresh, so anything to be done
     to the NEW markup waits for the header to say it has landed. */
  function afterRefresh(fn) {
    var ran = false;
    function run() {
      if (ran) { return; }
      ran = true;
      fn();
    }

    document.addEventListener('theme:loading:end', function () {
      window.setTimeout(run, 0);
    }, { once: true });
    window.setTimeout(run, REFRESH_CEILING);
  }

  /* ---------------------------------------------------------------- the look */

  function busy(root) {
    var timer = window.setTimeout(function () {
      root.classList.add('cart-discounts--busy');
    }, BUSY_AFTER);

    return function settled() {
      window.clearTimeout(timer);
      root.classList.remove('cart-discounts--busy');
    };
  }

  /* Re-adding a class does not re-run its animation; reading a layout property
     between the two does. */
  function replay(element, className) {
    element.classList.remove(className);
    void element.offsetWidth;
    element.classList.add(className);
  }

  function showError(root, message) {
    var slot = root.querySelector('[data-discount-error]');
    if (!slot) { return; }

    /* The mark is a sibling the server rendered, so the message goes in its own
       span rather than over the whole box. */
    var text = slot.querySelector('[data-discount-error-text]') || slot;
    text.textContent = message;
    slot.hidden = false;
    replay(slot, 'cart-discounts__error--in');

    var input = root.querySelector('[data-discount-input]');
    if (input) {
      input.setAttribute('aria-invalid', 'true');
      replay(input, 'cart-discounts__field--refused');
    }
  }

  function clearError(root) {
    var slot = root.querySelector('[data-discount-error]');
    if (slot) {
      var text = slot.querySelector('[data-discount-error-text]') || slot;
      slot.hidden = true;
      text.textContent = '';
      slot.classList.remove('cart-discounts__error--in');
    }

    var input = root.querySelector('[data-discount-input]');
    if (input) {
      input.removeAttribute('aria-invalid');
      input.classList.remove('cart-discounts__field--refused');
    }
  }

  function list(root) {
    var existing = root.querySelector('.cart-discounts__list');
    if (existing) { return existing; }

    /* A cart carrying nothing yet has no list to add to: the server only
       renders one when there is a row for it. */
    var fresh = document.createElement('ul');
    fresh.className = 'cart-discounts__list list--unstyled';
    fresh.setAttribute('role', 'list');

    var form = root.querySelector('.cart-discounts__form');
    if (form) { form.parentNode.insertBefore(fresh, form); }
    else { root.appendChild(fresh); }

    return fresh;
  }

  /* The row the shopper typed, on screen before the cart has answered. It
     carries no amount, because the amount is Shopify's to state, and it is
     replaced by the server's own row as soon as the refresh lands. */
  function optimisticRow(root, code) {
    var row = document.createElement('li');
    row.className = 'cart-discounts__row cart-discounts__row--pending';
    row.setAttribute('data-pending-code', code);

    var spinner = document.createElement('span');
    spinner.className = 'cart-discounts__spinner';
    spinner.setAttribute('aria-hidden', 'true');

    var body = document.createElement('span');
    body.className = 'cart-discounts__row-body';

    var name = document.createElement('span');
    name.className = 'cart-discounts__name';
    name.textContent = code;

    var note = document.createElement('span');
    note.className = 'cart-discounts__note';
    note.textContent = text(root, 'applying');

    body.appendChild(name);
    body.appendChild(note);
    row.appendChild(spinner);
    row.appendChild(body);

    list(root).appendChild(row);
    return row;
  }

  function dropRow(row) {
    if (!row || !row.parentNode) { return; }
    row.classList.add('cart-discounts__row--leaving');
    window.setTimeout(function () {
      if (row.parentNode) { row.parentNode.removeChild(row); }
    }, 170);
  }

  /* The confirmation, once the server's own row is in place: the row the code
     landed on, and the saving it changed. */
  function confirmLanded(code) {
    var root = document.querySelector('[data-cart-discounts]');
    if (!root) { return; }

    var rows = root.querySelectorAll('.cart-discounts__row');
    for (var i = 0; i < rows.length; i++) {
      var name = rows[i].querySelector('.cart-discounts__name');
      if (name && name.textContent.trim().toUpperCase() === code) {
        replay(rows[i], 'cart-discounts__row--landed');
        break;
      }
    }

    var saved = document.querySelector('.cart-discounts__saved');
    if (saved) { replay(saved, 'cart-discounts__saved--changed'); }
  }

  /* --------------------------------------------------------------- the moves */

  /* `introduced` is the codes this change is adding, and the only ones a
     refusal is reported for: a code already on the cart that Shopify has since
     stopped taking is dropped quietly, and the re-render tells that story
     better than an error line would. */
  function commit(root, codes, introduced) {
    var before = parseCodes(root.getAttribute('data-codes'));
    var settled = busy(root);

    return writeCodes(root, codes).then(function (cart) {
      var refused = refusedBy(cart, codes);
      if (refused.length === 0) {
        settled();
        if (!sameSet(codes, before)) { refresh(); }
        return { ok: true };
      }

      var kept = codes.filter(function (code) { return refused.indexOf(code) === -1; });
      var reported = refused.filter(function (code) {
        return (introduced || []).indexOf(code) !== -1;
      });

      /* Who outranked the new code, if anyone. A code that lost to another
         code on the cart is accepted on its own, so it is sent alone once to
         ask; one that lost to an automatic shows that automatic in the
         answer. Neither: the code itself was refused. The final write below
         puts the kept list back either way. */
      var outranked;
      if (reported.length > 0 && kept.length > 0) {
        outranked = writeCodes(root, [reported[0]]).then(function (alone) {
          if (refusedBy(alone, [reported[0]]).length === 0) { return kept.join(', '); }
          return automaticOrderDiscount(alone);
        }).catch(function () {
          /* No answer is only a plainer message; the kept list must still go
             back, or the cart is left holding the probe alone. */
          return '';
        });
      } else {
        outranked = Promise.resolve(reported.length > 0 ? automaticOrderDiscount(cart) : '');
      }

      return outranked.then(function (keptBy) {
        return writeCodes(root, kept).then(function () {
          settled();
          /* Re-rendering to the list already on screen would cost a request and
             wipe the error with it. */
          if (!sameSet(kept, before)) { refresh(); }
          return { ok: false, refused: reported, keptBy: keptBy };
        });
      });
    }).catch(function () {
      settled();
      return { ok: false, unreachable: true };
    });
  }

  function apply(root) {
    var input = root.querySelector('[data-discount-input]');
    if (!input) { return; }

    var typed = parseCodes(input.value);
    if (typed.length === 0) {
      showError(root, text(root, 'empty'));
      input.focus();
      return;
    }

    var held = parseCodes(root.getAttribute('data-codes'));
    var already = typed.filter(function (code) { return held.indexOf(code) !== -1; });
    if (already.length === typed.length) {
      showError(root, text(root, 'duplicate', already[0]));
      input.focus();
      input.select();
      return;
    }

    var adding = typed.filter(function (code) { return held.indexOf(code) === -1; });
    var codes = held.concat(adding);

    clearError(root);

    var rows = adding.map(function (code) { return optimisticRow(root, code); });
    var button = root.querySelector('[data-action="apply-discount"]');
    if (button) { button.disabled = true; }

    commit(root, codes, adding).then(function (result) {
      if (button) { button.disabled = false; }

      if (result.ok) {
        input.value = '';
        toggleForm(root, false);
        afterRefresh(function () { confirmLanded(adding[adding.length - 1]); });
        return;
      }

      rows.forEach(dropRow);

      var refusedCode = result.refused && result.refused[0] || adding[0];
      if (result.unreachable) { showError(root, text(root, 'unreachable')); }
      else if (result.keptBy) { showError(root, text(root, 'outranked', refusedCode, result.keptBy)); }
      else { showError(root, text(root, 'refused', refusedCode)); }

      input.focus();
      input.select();
    });
  }

  function remove(root, button) {
    var row = button.closest('.cart-discounts__row');
    button.disabled = true;
    clearError(root);

    if (row) { row.classList.add('cart-discounts__row--leaving'); }

    commit(root, parseCodes(button.getAttribute('data-remaining'))).then(function (result) {
      if (result.ok) { return; }

      /* Nothing was taken off, so the row comes back rather than leaving the
         cart looking lighter than it is. */
      if (row) { row.classList.remove('cart-discounts__row--leaving'); }
      button.disabled = false;
      if (result.unreachable) { showError(root, text(root, 'unreachable')); }
    });
  }

  function toggleForm(root, open) {
    var form = root.querySelector('.cart-discounts__form');
    var button = root.querySelector('[data-action="toggle-discount-form"]');
    if (!form) { return; }

    var opening = typeof open === 'boolean' ? open : form.hasAttribute('hidden');
    if (opening) {
      form.removeAttribute('hidden');
      replay(form, 'cart-discounts__form--in');
    } else {
      form.setAttribute('hidden', '');
      form.classList.remove('cart-discounts__form--in');
      clearError(root);
    }

    if (button) { button.setAttribute('aria-expanded', String(opening)); }

    if (opening) {
      var input = form.querySelector('[data-discount-input]');
      if (input) { input.focus(); }
    }
  }

  /* ---------------------------------------------------------- the claim link */

  function openMiniCart() {
    var toggle = document.querySelector('[data-action="toggle-mini-cart"]');
    if (toggle && toggle.getAttribute('aria-expanded') !== 'true') {
      toggle.click();
    }
  }

  /* The footer's claim link. Its href stores the code the only way a browser can
     on its own -- a navigation to /discount/ -- which costs the shopper the page
     they were on. Fetching the same URL stores it just as well, because the
     cookie rides the 302, and then the cart comes to them. ?redirect= points at
     /cart.js so what the fetch follows to is a few hundred bytes of JSON instead
     of a whole rendered page.

     Left alone when there is no drawer to open -- a 'page' cart, or the cart page
     itself -- because there the href is already the right answer. */
  function claim(event, link) {
    if (link.getAttribute('aria-busy') === 'true') { return; }
    if (!document.querySelector('[data-action="toggle-mini-cart"]')) { return; }

    event.preventDefault();
    link.setAttribute('aria-busy', 'true');

    fetch(link.pathname + '?redirect=' + encodeURIComponent('/cart.js'), {
      credentials: 'same-origin'
    }).then(function () {
      link.removeAttribute('aria-busy');
      afterRefresh(openMiniCart);
      refresh();
    }, function () {
      /* The code matters more than staying put: hand the click back to the href. */
      window.location.assign(link.href);
    });
  }

  /* Delegated from the document: the cart drawer and the cart section are both
     re-fetched and replaced on every cart change, so anything bound to the
     block itself would be thrown away with it. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-action="apply-discount"], [data-action="remove-discount"], [data-action="toggle-discount-form"], [data-action="claim-discount"]');
    if (!trigger) { return; }

    var action = trigger.getAttribute('data-action');

    /* The claim link is the one control here that lives outside the block --
       it is in the footer ticket, with no cart markup around it. */
    if (action === 'claim-discount') {
      claim(event, trigger);
      return;
    }

    var root = trigger.closest('[data-cart-discounts]');
    if (!root) { return; }

    if (action === 'apply-discount') {
      apply(root);
    } else if (action === 'remove-discount') {
      remove(root, trigger);
    } else {
      toggleForm(root);
    }
  });

  /* Enter in the field means Apply. The block deliberately opens no form of its
     own -- both hosts already wrap it in the cart form, and a nested one is
     dropped by the browser -- so there is no submit event to lean on, and
     without this the keypress would submit the CART instead. */
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Enter') { return; }
    if (!event.target.matches('[data-discount-input]')) { return; }

    event.preventDefault();
    var root = event.target.closest('[data-cart-discounts]');
    if (root) { apply(root); }
  });

  /* Typing again is the shopper answering the error, so it goes. */
  document.addEventListener('input', function (event) {
    if (!event.target.matches('[data-discount-input]')) { return; }
    var root = event.target.closest('[data-cart-discounts]');
    if (root) { clearError(root); }
  });
}());
